//윈터스쿨 셀프 결제 금액 계산 — 서버(peetsunbaeserver src/control/winterPrice.ts)와 같은 코드.
//화면 표시용. 최종 금액은 서버 계산이 기준이며 제출 시 clientPrice 가 다르면 서버가 priceMismatch 로 거절한다.
//설정(금액표·기간·혜택)은 서버 GET /dashboard/payments/winterpay/data 의 config.rates 를 그대로 넣는다.

export type WinterStudentType = "general" | "student";
export interface WinterDateRange { start: string; end: string; }
export interface WinterPeriod { id: string; name: string; target: "all" | "student" | "general"; ranges: WinterDateRange[]; }
export interface WinterBenefit { id: string; name: string; type: "until" | "on" | "always"; dates: string[]; pct: number; periodIds: string[]; }
export interface WinterBranchPrice { base: number; periods: { [periodId: string]: number | null }; }
export interface WinterRates { prices: { [english: string]: WinterBranchPrice }; periods: WinterPeriod[]; benefits: WinterBenefit[]; }
export interface WinterProgram { pid: string; name: string; startFrom: string; startTo: string; ends: string[]; fixedStart?: { branches: string[]; date: string }; }
export interface WinterBranch { english: string; korean: string; bankinfo: { accountName: string; bankName: string; accountNumber: string } | null; }
export interface WinterConfig { rates: WinterRates; programs: WinterProgram[]; branches: WinterBranch[]; seatPerk: { lastStart: string }; now: number; today: string; }

export const isValidPrice = (v: any) => Number.isInteger(v) && v > 0;
export const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
export const floor10 = (v: number) => 10 * Math.floor(v / 10);
export const dayNum = (d: Date) => Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 864e5);
export const toDate = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
export const fromDayNum = (n: number) => { const d = new Date(n * 864e5); return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); };
export const pad2 = (n: number) => String(n).padStart(2, "0");
export const ymd = (d: Date) => d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
export const isYmd = (v: any) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && ymd(toDate(v)) === v;
const semOk = (p: any) => !!p && isYmd(p.start) && isYmd(p.end) && dayNum(toDate(p.start)) <= dayNum(toDate(p.end));
const toRanges = (list: any[]) => (list || []).filter(semOk).map(p => [dayNum(toDate(p.start)), dayNum(toDate(p.end))] as [number, number]);
const inRanges = (r: [number, number][], n: number) => r.some(([x, y]) => n >= x && n <= y);
export const won = (v: number) => v.toLocaleString("ko-KR") + "원";

export const calcProrated = (monthlyPrice: number | null, start: Date | null, end: Date | null, discount: number = 0): number | null => {
    if (!isValidPrice(monthlyPrice) || !start || !end) return null;
    if (dayNum(start) > dayNum(end)) return null;
    const base = floor10((monthlyPrice as number) * (100 - discount) / 100);
    let total = 0;
    let y = start.getFullYear(), m = start.getMonth();
    while (dayNum(new Date(y, m, 1)) <= dayNum(end)) {
        const mStart = new Date(y, m, 1);
        const mEnd = new Date(y, m, daysInMonth(y, m));
        const s = dayNum(start) > dayNum(mStart) ? start : mStart;
        const e = dayNum(end) < dayNum(mEnd) ? end : mEnd;
        if (dayNum(s) <= dayNum(e)) {
            const F = dayNum(e) - dayNum(s) + 1;
            const w = daysInMonth(y, m);
            total += F === w ? base : floor10(base / w * F);
        }
        m++; if (m > 11) { m = 0; y++; }
    }
    return total;
};

export const benefitMet = (c: WinterBenefit, payDate: string) => {
    if (c.type === "always") return true;
    if (!isYmd(payDate)) return false;
    const pay = dayNum(toDate(payDate));
    return c.type === "until" ? pay <= dayNum(toDate(c.dates[0])) : c.dates.some(d => dayNum(toDate(d)) === pay);
};

export interface RateArgs { rates: WinterRates; type: WinterStudentType; branch: string; start: Date | null; end: Date | null; extra?: number; activeIds?: string[]; }
export interface RateSegment { from: number; to: number; pid: string | null; name: string; monthly: number; discount: number; bPct: number; benefit: boolean; bIds: string[]; start: Date; end: Date; }

export const rateSegments = ({ rates, type, branch, start, end, extra = 0, activeIds = [] }: RateArgs): RateSegment[] => {
    const row = rates && rates.prices[branch];
    if (!row || !start || !end || dayNum(start) > dayNum(end)) return [];
    const ps = rates.periods.map(p => ({ p, r: toRanges(p.ranges) }));
    const act = rates.benefits.filter(c => activeIds.includes(c.id));
    const segs: any[] = [];
    for (let n = dayNum(start); n <= dayNum(end); n++) {
        const hit = ps.find(x => (x.p.target === "all" || x.p.target === type) && inRanges(x.r, n));
        const p = hit ? hit.p : null;
        const monthly = p && isValidPrice(row.periods[p.id]) ? (row.periods[p.id] as number) : row.base;
        const here = act.filter(c => !c.periodIds.length || (p && c.periodIds.includes(p.id)));
        const bPct = here.length ? Math.max(...here.map(c => c.pct)) : 0;
        const discount = Math.max(extra, bPct);
        const pid = p ? p.id : null;
        const last = segs[segs.length - 1];
        if (last && last.pid === pid && last.monthly === monthly && last.discount === discount) last.to = n;
        else segs.push({ from: n, to: n, pid, name: p ? p.name : "기본", monthly, discount, bPct, benefit: bPct > 0, bIds: here.map(c => c.id) });
    }
    return segs.map(g => ({ ...g, start: fromDayNum(g.from), end: fromDayNum(g.to) }));
};

export const calcRate = (args: RateArgs): number | null => {
    const segs = rateSegments(args);
    if (!segs.length) return null;
    let total = 0;
    for (const g of segs) {
        const v = calcProrated(g.monthly, g.start, g.end, g.discount);
        if (v == null) return null;
        total += v;
    }
    return total;
};

export interface ProrateRow { y: number; m: number; s: Date; e: Date; F: number; w: number; amount: number; }
export const prorateBreakdown = (monthlyPrice: number, start: Date, end: Date, discount: number = 0): ProrateRow[] => {
    if (!isValidPrice(monthlyPrice) || !start || !end || dayNum(start) > dayNum(end)) return [];
    const base = floor10(monthlyPrice * (100 - discount) / 100);
    const rows: ProrateRow[] = [];
    let y = start.getFullYear(), m = start.getMonth();
    while (dayNum(new Date(y, m, 1)) <= dayNum(end)) {
        const mStart = new Date(y, m, 1), mEnd = new Date(y, m, daysInMonth(y, m));
        const s = dayNum(start) > dayNum(mStart) ? start : mStart;
        const e = dayNum(end) < dayNum(mEnd) ? end : mEnd;
        if (dayNum(s) <= dayNum(e)) {
            const F = dayNum(e) - dayNum(s) + 1, w = daysInMonth(y, m);
            rows.push({ y, m, s, e, F, w, amount: F === w ? base : floor10(base / w * F) });
        }
        m++; if (m > 11) { m = 0; y++; }
    }
    return rows;
};

//고객 화면용: 오늘(서버가 준 today) 기준 혜택 자동 판단
export interface CustomerPriceResult {
    price: number | null;
    original: number | null;
    usedBenefits: { id: string; name: string; pct: number }[];
    segments: RateSegment[];
    rows: { month: string; range: string; days: string; amount: string }[];
}
export const calcCustomerPrice = (rates: WinterRates, type: WinterStudentType, branch: string, start: Date | null, end: Date | null, payDate: string): CustomerPriceResult => {
    const activeIds = rates.benefits.filter(c => benefitMet(c, payDate)).map(c => c.id);
    const args: RateArgs = { rates, type, branch, start, end, extra: 0, activeIds };
    const price = calcRate(args);
    const original = calcRate({ ...args, activeIds: [] });
    const segs = rateSegments(args);
    const usedIds = new Set<string>();
    segs.forEach(g => g.bIds.forEach(id => usedIds.add(id)));
    const usedBenefits = rates.benefits.filter(c => usedIds.has(c.id)).map(c => ({ id: c.id, name: c.name, pct: c.pct }));
    const multi = new Set(segs.map(g => g.name)).size > 1 || (segs.length > 0 && segs[0].name !== "기본");
    const md = (d: Date) => (d.getMonth() + 1) + "." + d.getDate();
    const rows: CustomerPriceResult["rows"] = [];
    segs.forEach(g => prorateBreakdown(g.monthly, g.start, g.end, g.discount).forEach(r => rows.push({
        month: r.y + "년 " + (r.m + 1) + "월" + (multi ? " · " + g.name : "") + (g.discount ? " · " + g.discount + "% 할인" : ""),
        range: md(r.s) + " ~ " + md(r.e),
        days: r.F === r.w ? "한 달 전체 · " + r.w + "일" : r.F + "일 / " + r.w + "일",
        amount: won(r.amount),
    })));
    return { price, original, usedBenefits, segments: segs, rows };
};
